import { dinero, traducirError, useTraduccion } from '@tallerya/i18n';
import { AvisoError, Ayuda, Boton, Chip, Menu, useAvisos, type ElementoMenu } from '@tallerya/ui';
import { useState, type ReactNode } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router';
import { useTituloPantalla } from '../../layout/titulo';
import { useMoneda } from '../../datos/useMoneda';
import { ConfirmacionItem, type AccionItem } from './ConfirmacionItem';
import { useCambiosItem, useCategorias, useFichaItem, version, type FichaItem as Ficha } from './datos';
import { horas } from './duracion';
import { encabezado } from './encabezado';
import { PanelHistorial } from './PanelHistorial';

// La ficha de un producto o servicio (#157): consultarlo sin abrirlo como si se
// fuera a cambiar. Sigue a `FichaCliente.tsx` —migas, cabecera con sus acciones
// y secciones de solo lectura— y al frame «Productos/Servicios - Ficha» del
// Figma.
//
// Las acciones son las del menú de la fila del listado: «Editar» a la vista y
// el resto en el «•••». Desactivar y Eliminar piden confirmación con las mismas
// ventanas (`ConfirmacionItem`); Activar va directo.

const RUTA = '/productos-servicios';

export function FichaItem() {
  const { t } = useTraduccion();
  const navegar = useNavigate();
  const { id } = useParams();
  const consulta = useFichaItem(id);
  useTituloPantalla(consulta.data?.name ?? t('admin.catalogo.titulo'));

  // Si otro la eliminó, la API responde que no existe y la página lo dice. Al
  // eliminar desde aquí no se llega a ver: `eliminar` no vuelve a pedir la ficha
  // eliminada, así que se sigue viendo hasta que llega el listado.
  const ficha = consulta.data;

  if (consulta.isPending) return <p className="entrada">{t('app.cargando')}</p>;

  if (!ficha) {
    const noExiste = consulta.data === null;
    return (
      <section className="aviso">
        <h2>{noExiste ? t('admin.catalogo.noExiste.titulo') : t('ui.listado.error.titulo')}</h2>
        <p>{noExiste ? t('admin.catalogo.noExiste.texto') : t('ui.listado.error.texto')}</p>
        <Boton variante="secundario" onClick={() => void navegar(RUTA)}>
          {t('admin.catalogo.volver')}
        </Boton>
      </section>
    );
  }

  return <Contenido ficha={ficha} />;
}

function Contenido({ ficha }: { ficha: Ficha }) {
  const { t, idioma } = useTraduccion();
  const navegar = useNavigate();
  const { avisar } = useAvisos();
  const cambios = useCambiosItem();
  const categorias = useCategorias();
  const moneda = useMoneda();
  const id = String(ficha.id);
  const servicio = ficha.kind === 'service';
  const ordenes = Number(ficha.orderCount);

  // Si el listado está detrás: volver con «Atrás» le conserva los filtros.
  const desdeListado = (useLocation().state as { desdeListado?: boolean } | null)?.desdeListado ?? false;
  const alListado = () => void (desdeListado ? navegar(-1) : navegar(RUTA));

  const [confirmacion, setConfirmacion] = useState<{ accion: AccionItem; id: string } | null>(null);
  const [problema, setProblema] = useState<string | null>(null);
  // El historial empieza cerrado y no se pide hasta abrirlo (#193).
  const [viendoHistorial, setViendoHistorial] = useState(false);

  // La lista solo trae las activas: la que ya no está se nombra como en el
  // formulario. Mientras carga, nada, antes que un nombre que cambia.
  const categoria = !ficha.categoryId
    ? t('admin.catalogo.formulario.sinCategoria')
    : !categorias.data
      ? undefined
      : (categorias.data.find((c) => String(c.id) === String(ficha.categoryId))?.name ??
        t('admin.catalogo.formulario.categoriaInactiva'));

  const precio =
    ficha.listPrice === null || ficha.listPrice === undefined
      ? '—'
      : moneda
        ? dinero(Number(ficha.listPrice), moneda, idioma)
        : Number(ficha.listPrice).toFixed(2);

  // `desdeListado`: el formulario vuelve con «Atrás», que es esta ficha.
  const editar = () => void navegar(`${RUTA}/${id}/editar`, { state: { desdeListado: true } });
  const duplicar = () => void navegar(`${RUTA}/nuevo?duplicar=${id}`, { state: { desdeListado: true } });

  async function activar() {
    setProblema(null);
    try {
      const fallo = await cambios.cambiarEstado(id, version(ficha.version), true);
      if (fallo) {
        setProblema(fallo.code ? traducirError(fallo.code, t) : t('admin.catalogo.error.reintentar'));
        return;
      }
      avisar({ titulo: t('admin.catalogo.aviso.activado', { nombre: ficha.name }) });
    } catch {
      setProblema(t('admin.catalogo.error.reintentar'));
    }
  }

  const masAcciones: ElementoMenu[] = [
    { etiqueta: t('admin.catalogo.menu.duplicar'), onSeleccionar: duplicar },
    { tipo: 'divisor' },
    ficha.isActive
      ? { etiqueta: t('admin.catalogo.menu.desactivar'), onSeleccionar: () => setConfirmacion({ accion: 'desactivar', id }) }
      : { etiqueta: t('admin.catalogo.menu.activar'), onSeleccionar: () => void activar() },
    { etiqueta: t('admin.catalogo.menu.eliminar'), peligro: true, onSeleccionar: () => setConfirmacion({ accion: 'eliminar', id }) },
  ];

  return (
    <>
      <nav aria-label={t('admin.catalogo.ficha.migas')}>
        <ol className="ficha__migas">
          <li>
            <Link to={RUTA}>{t('admin.catalogo.titulo')}</Link>
          </li>
          <li aria-current="page">{ficha.name}</li>
        </ol>
      </nav>

      {problema && <AvisoError aviso={{ titulo: t('admin.catalogo.error.titulo'), texto: problema }} />}

      <header className="tipos__cabecera">
        <div>
          <div className="tipos__titulo">
            <h1>{ficha.name}</h1>
            <Ayuda tema={ficha.name} texto={t('admin.catalogo.ficha.info')} tono="titulo" />
            {servicio ? (
              <Chip tono="marca">{t('admin.catalogo.servicio')}</Chip>
            ) : (
              <Chip>{t('admin.catalogo.producto')}</Chip>
            )}
            {ficha.isActive ? (
              <Chip tono="exito">{t('admin.catalogo.activo')}</Chip>
            ) : (
              <Chip>{t('admin.catalogo.inactivo')}</Chip>
            )}
          </div>
          <p className="tipos__resumen">{encabezado(ficha, idioma, t, categoria)}</p>
        </div>
        <div className="tipos__acciones">
          <Boton variante="secundario" onClick={() => setViendoHistorial(true)}>
            {t('admin.historial.titulo')}
          </Boton>
          <Boton variante="secundario" onClick={editar}>
            {servicio ? t('admin.catalogo.editar.titulo') : t('admin.catalogo.editar.tituloProducto')}
          </Boton>
          <Menu
            elementos={masAcciones}
            disparador={({ alternar, atributos }) => (
              <Boton variante="secundario" soloIcono aria-label={t('admin.catalogo.mas')} onClick={alternar} {...atributos}>
                •••
              </Boton>
            )}
          />
        </div>
      </header>

      <div className="ficha__secciones">
        <Seccion titulo={t('admin.catalogo.formulario.precioYTiempo')}>
          <dl className="ficha__datos">
            <Dato etiqueta={t('admin.catalogo.formulario.precio')}>{precio}</Dato>
            {/* Los productos no llevan duración: se entregan, no se trabajan. */}
            {servicio && (
              <Dato etiqueta={t('admin.catalogo.formulario.duracion')}>
                {horas(ficha.referenceDurationMinutes === null ? null : Number(ficha.referenceDurationMinutes), idioma)}
              </Dato>
            )}
            <Dato etiqueta={t('admin.catalogo.formulario.codigoExterno')}>{ficha.externalCode ?? '—'}</Dato>
          </dl>
        </Seccion>

        <Seccion titulo={t('admin.catalogo.formulario.voz')}>
          {ficha.aliases.length > 0 ? (
            <ul className="ficha__pastillas">
              {ficha.aliases.map((a) => (
                <li key={a}>{a}</li>
              ))}
            </ul>
          ) : (
            <p className="ficha__nota">{t('admin.catalogo.ficha.sinAlias')}</p>
          )}
        </Seccion>

        {/* En cuántas órdenes está: es lo que decide si se puede eliminar. */}
        <Seccion titulo={t('admin.catalogo.ficha.uso')}>
          <div className="confirmar__caja">
            <strong>
              {ordenes === 0
                ? t('admin.catalogo.eliminar.sinOrdenes')
                : ordenes === 1
                  ? t('admin.catalogo.desactivar.unaOrden')
                  : t('admin.catalogo.desactivar.ordenes', { n: ordenes })}
            </strong>
            <p>
              {ordenes === 0
                ? t('admin.catalogo.ficha.sinOrdenesTexto')
                : ficha.isActive
                  ? t('admin.catalogo.ficha.conOrdenesTexto')
                  : t('admin.catalogo.ficha.conOrdenesInactivo')}
            </p>
          </div>
        </Seccion>

        {/* Quién le cambió qué (#181), en el panel que abre «Historial», como en la ficha del cliente (#193). */}
        <PanelHistorial itemId={id} abierto={viendoHistorial} onCerrar={() => setViendoHistorial(false)} />
      </div>

      <ConfirmacionItem
        pedido={confirmacion}
        onCerrar={() => setConfirmacion(null)}
        onHecho={(accion) => {
          if (accion === 'eliminar') alListado();
        }}
      />
    </>
  );
}

function Seccion({ titulo, children }: { titulo: string; children: ReactNode }) {
  return (
    <section className="tipos__seccion">
      <div className="tipos__seccion-cabecera">
        <h2>{titulo}</h2>
      </div>
      {children}
    </section>
  );
}

function Dato({ etiqueta, children }: { etiqueta: string; children: ReactNode }) {
  return (
    <div className="ficha__dato">
      <dt>{etiqueta}</dt>
      <dd>{children}</dd>
    </div>
  );
}
